import { Group, UserAvatar } from '@/components/_common';
import moment from 'moment';
import React, { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';
import StatusTag from '@/components/_common/StatusTag';
import { Avatar, Button, Divider, Modal } from 'antd';
import { useDisclosure } from '@/hooks';
import ScheduleEditModal from '../ScheduleEditModal';
import { GPActivityColumnItem } from '@/core/domain/visual-management/activities/activities.types';
import { FaExternalLinkAlt } from 'react-icons/fa';
import {
  useActivityDelete,
  useActivityGenerateWorkOrder,
} from '@/core/domain/visual-management/activities/activities.hooks';
import classNames from 'classnames';
import GenerateWorkOrderWithPolicy from './GenerateWorkOrderWithPolicy';
import { isAppPortocar } from '@/index';
import './style.less';

interface Props {
  task: GPActivityColumnItem;
  index: number;
}

const Task: React.FC<Props> = ({ task, index }) => {
  const [colorScheme] = useColorScheme();
  const [modalEditOpened, modalEditAction] = useDisclosure();
  const [activityId, setActivityId] = useState<string>();

  const [createOpened, createAction] = useDisclosure();

  const isDateOk =
    !task.estimatedLeaveDate ||
    moment(task.estimatedLeaveDate).isAfter(moment());

  const generateWorkorder = useActivityGenerateWorkOrder();
  const deleteActivity = useActivityDelete();

  const handleOpenModal = (activityId: string) => {
    if (activityId) {
      setActivityId(activityId);

      modalEditAction.open();
    }
  };

  const createWorkOrder = async (activityId: string, vehicleId?: number) => {
    await generateWorkorder.mutateAsync({ activityId, vehicleId });
    handleOpenModal(activityId);
  };

  const handleCreateWorkOrderBySchedule = async (activityId: string) => {
    Modal.confirm({
      title: 'Criação do orçamento',
      content:
        'Deseja realmente criar um orçamento a partir deste agendamento?',
      onOk: async () => createWorkOrder(activityId),
      okText: 'Criar',
    });
  };

  const handleRemoveActivity = async (activityId: string) => {
    Modal.confirm({
      title: 'Exclusão de Agendamento',
      content:
        'Deseja realmente excluir o agendamento, esse processo será irreversível.',
      onOk: async () => {
        await deleteActivity.mutateAsync(activityId);
      },
      okText: 'Excluir',
      okType: 'danger',
    });
  };

  const getVehicleInfo = () => (
    <div
      title={`${task.licensePlate} • ${task.vehicle}`}
      className={`kanban-task-vehicle ${colorScheme}`}
    >
      {`${task.licensePlate} • ${task.vehicle}`}
    </div>
  );

  return (
    <>
      <Draggable draggableId={String(task.id)} index={index}>
        {(provided, snapshop) => (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`kanban-task ${colorScheme}`}
            style={{
              ...provided.draggableProps.style,
              backgroundColor: colorScheme === 'dark' ? '#313131' : 'white',
              boxShadow: snapshop.isDragging
                ? '0px 0px 20px rgba(0,0,0,0.3)'
                : '',
              height: 'auto',
            }}
            ref={provided.innerRef}
            onClick={() => handleOpenModal(task.id)}
          >
            {!!task.licensePlate && getVehicleInfo()}

            <div
              title={task.customer}
              className={classNames('kanban-task-people-name', colorScheme, {
                'with-title': !task.licensePlate,
              })}
            >
              {task.customer}
            </div>

            <Group style={{ margin: '8px 0px' }} position="between">
              <StatusTag
                label={moment(task.estimatedLeaveDate).format('DD/MM/YY')}
                color={isDateOk ? 'blue' : 'red'}
              />

              <Avatar.Group maxCount={2}>
                {task.technicians?.map((tech) => (
                  <UserAvatar
                    key={tech.id}
                    picture={tech.metadata?.picture ?? undefined}
                  />
                ))}
              </Avatar.Group>
            </Group>

            <Divider style={{ marginTop: 15, marginBottom: 5 }} />

            {task.workOrderComplementId === null ? (
              <Group position="between">
                <Button
                  type="link"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (isAppPortocar) {
                      createAction.open();
                    } else {
                      handleCreateWorkOrderBySchedule(task.id);
                    }
                  }}
                >
                  Gerar orçamento
                </Button>

                <Button
                  type="link"
                  danger
                  onClick={(e) => {
                    e.stopPropagation();

                    handleRemoveActivity(task.id);
                  }}
                >
                  Excluir
                </Button>
              </Group>
            ) : (
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/app/movements/workOrders/${task.workOrderId}/complements/${task.workOrderComplementId}/generalInfo`,
                    '_blank',
                  );
                }}
              >
                <Group gap={8}>
                  Orçamento <FaExternalLinkAlt fontSize={10} />
                </Group>
              </Button>
            )}
          </div>
        )}
      </Draggable>

      <ScheduleEditModal
        activityId={activityId}
        open={modalEditOpened}
        onCancel={modalEditAction.close}
      />

      <GenerateWorkOrderWithPolicy
        activityId={task.id}
        opened={createOpened}
        onClose={createAction.close}
        onFinish={() => handleOpenModal(task.id)}
      />
    </>
  );
};

export default Task;
